import { type MouseEvent } from 'react'
import { type NavigateOptions } from 'react-router'
import { useNavigate, useSearchParams } from 'react-router'
import { ONBOARDING_URL } from 'config/config'
import { emailCaptureManager } from 'services/EmailCaptureManager'

const isExternalUrl = (url: string) => /^(https?:)?\/\//.test(url)

type CtaTarget = {
  url: string
  withQuiz?: boolean
  quizNavOptions?: NavigateOptions
}

export default function useHomePageCta() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const redirectToOnboarding = (
    redirectTo: string,
    navOptions?: NavigateOptions
  ) => {
    const params = new URLSearchParams(searchParams)
    params.set('redirectTo', redirectTo)
    navigate(
      { pathname: ONBOARDING_URL, search: params.toString() },
      navOptions
    )
  }

  const routeThroughQuizIfNeeded = ({
    url,
    withQuiz = true,
    quizNavOptions,
  }: CtaTarget) => {
    if (!withQuiz || emailCaptureManager.passedAnyOnboarding()) return false
    redirectToOnboarding(url, quizNavOptions)
    return true
  }

  const navigateToCta = ({
    navOptions,
    ...target
  }: CtaTarget & { navOptions?: NavigateOptions }) => {
    if (routeThroughQuizIfNeeded(target)) return
    if (isExternalUrl(target.url)) window.location.assign(target.url)
    else navigate(target.url, navOptions)
  }

  const handleCtaLinkClick = (
    event: MouseEvent<HTMLElement>,
    target: CtaTarget
  ) => {
    if (routeThroughQuizIfNeeded(target)) event.preventDefault()
  }

  return { navigateToCta, handleCtaLinkClick }
}
